import React from 'react';
import {NativeScrollEvent, NativeSyntheticEvent} from 'react-native';

interface HandleScrollOptions {
  disabled?: boolean;
  threshold?: number;
  onLoadMore: (nextPage: number) => void;
}

const defaultThreshold = 120;

const useResultScrollLoadMore = <T>() => {
  const currentPageRef = React.useRef(1);
  const hasMoreRef = React.useRef(true);
  const listRef = React.useRef<T[]>([]);
  const loadingMoreRef = React.useRef(false);

  const reset = (
    setList?: React.Dispatch<React.SetStateAction<T[]>>,
    clearList = true,
  ) => {
    currentPageRef.current = 1;
    hasMoreRef.current = true;
    loadingMoreRef.current = false;
    listRef.current = [];
    if (clearList) {
      setList?.([]);
    }
  };

  const syncList = (
    currentList: T[],
    loadMore: boolean,
    setList: React.Dispatch<React.SetStateAction<T[]>>,
  ) => {
    const nextList = loadMore
      ? listRef.current.concat(currentList)
      : currentList;
    listRef.current = nextList;
    setList(nextList);
    return nextList;
  };

  const rollbackPage = () => {
    currentPageRef.current = Math.max(1, currentPageRef.current - 1);
  };

  const stopLoadingMore = () => {
    loadingMoreRef.current = false;
  };

  const handleScroll = (
    e: NativeSyntheticEvent<NativeScrollEvent>,
    {
      disabled = false,
      threshold = defaultThreshold,
      onLoadMore,
    }: HandleScrollOptions,
  ) => {
    if (disabled || loadingMoreRef.current || !hasMoreRef.current) {
      return;
    }

    const {layoutMeasurement, contentOffset, contentSize} = e.nativeEvent;
    const distanceToBottom =
      contentSize.height - (layoutMeasurement.height + contentOffset.y);

    if (distanceToBottom < threshold) {
      loadingMoreRef.current = true;
      currentPageRef.current += 1;
      onLoadMore(currentPageRef.current);
    }
  };

  return {
    currentPageRef,
    hasMoreRef,
    listRef,
    reset,
    syncList,
    rollbackPage,
    stopLoadingMore,
    handleScroll,
  };
};

export default useResultScrollLoadMore;
